import { UUID } from './data-types'
import type { FileClassificationTypeEnum } from './document'
import { TTruckType } from './equipment'
import { IUpdatedBy } from './shipment'
import { TShipmentStatus } from './shipment-status'
import { IPublicStop } from './stop'
import { ChaineWeightType } from './weight'
import { IWorkspace } from './workspace'

export interface IChatUser {
  banned?: boolean
  email: string | null
  name: string | null
  online?: boolean
  phone: string | null
  photo?: string | null
  role?: string | null
  userID: UUID
  /**
   * The workspace ID of the user as it is listed for that specific chat channel when they sent the message
   */
  workspaceID?: UUID
}

export interface IChatMember extends IChatUser {
  channelRole: string | null
  isUserInChannelOwnerWorkspace?: boolean
  userJoinedChannelAt?: string
  userRoleInShipmentChannel?: ShipmentChannelRoleEnum
  /**
   * The workspace ID of the member if they were added to the channel and a workspace was specified
   */
  workspaceID?: UUID
}

/**
 * The user who sent a shipment chat message (to be used for assistance requested, exception detected events)
 */
export interface IShipmentChatUser extends IChatUser {
  isUserInChannelOwnerWorkspace: boolean
  userRoleInShipmentChannel: ShipmentChannelRoleEnum
}

export enum ChaineChannelTypeEnum {
  BookingConversation = 'BookingConversation',
  Default = 'Default',
  Driver = 'Driver',
  Offer = 'Offer',
  Shipment = 'Shipment'
}

export interface IShipmentChatMetadata {
  chaineChannelType: ChaineChannelTypeEnum
  destinationCity?: string
  destinationState?: string
  originCity?: string
  originState?: string
  shipmentID: string
  shipmentNumber?: string
  shipmentOwnerEnterpriseWorkspaceID?: UUID
  shipmentOwnerWorkspaceID?: UUID
  shipmentStatus?: TShipmentStatus
  shipmentStatusTime?: string
  updatedBy?: IUpdatedBy
  /**
   * @deprecated Use shipmentOwnerWorkspaceID instead
   */
  workspaceID?: UUID
}

export interface IPostChatMetadata {
  commodity: string | null
  id: string
  loadNumber: string | null
  stops: IPublicStop[] | null
  truckType: TTruckType[] | null
  weight: ChaineWeightType | null
  workspace: IWorkspace
}

export interface IPostOfferChatMetadata {
  offerID: string
  offeredBy: string
  post: IPostChatMetadata
  postOwnerEnterpriseWorkspaceID?: UUID
  postOwnerWorkspaceID: UUID
}

export enum AttachmentTypeEnum {
  Audio = 'audio',
  File = 'file',
  Image = 'image',
  Text = 'text',
  Video = 'video'
}

/**
 * This is the custom message type, which is used to identify the type of message on the client side.
 * 'Default' is a regular message
 * 'Event' is a message that is an event, and it will require an 'actionType'. For example, if a shipment is created, then the message type will be 'Event', and the message will state "{user.name} created the shipment". If messageType is 'Event', then the 'actionType' is required.
 * 'Template' us when you provide a message type of "HTML" or "Markdown". This is used by the client.
 *
 */
export enum SendChatMessageMessageTypeEnum {
  Default = 'default',
  Event = 'event',
  Template = 'template'
}

/**
 * This is required if messageType = 'Event'
 * This is the custom event type of the message, which is used on client side to identify the event message. Currently, we are using this for "System" messages when the 'from' is Chaine.
 */
export enum SendChatMessageEventEnum {
  AppDownloadMessageCancelled = 'appDownloadMessageCancelled',
  AppDownloadMessageCompleted = 'appDownloadMessageCompleted',

  AppDownloadMessageScheduled = 'appDownloadMessageScheduled',
  DemoEvent = 'demoEvent',
  MemberAdded = 'memberAdded',
  MemberRemoved = 'memberRemoved',
  PostOfferCreated = 'postOfferCreated',
  ShipmentCreated = 'shipmentCreated',
  ShipmentExceptionDetected = 'shipmentExceptionDetected',
  ShipmentPhoneCallCompleted = 'shipmentPhoneCallCompleted',
  ShipmentRuleScheduled = 'shipmentRuleScheduled',
  ShipmentUpdated = 'shipmentUpdated',
  StopArrived = 'stopArrived',
  StopDeparted = 'stopDeparted',
  StopETAUpdated = 'stopETAUpdated'
}

export interface IAttachment {
  assetURL: string
  /**
   * This is the classification of the attachment based on AI.
   */
  classificationType?: FileClassificationTypeEnum
  fileSize: number
  filename: string
  mimeType: string
  thumbURL?: string
  type: AttachmentTypeEnum
}

/**
 * @deprecated Use IMessage instead
 */
export interface IChatMessage {
  actionType: string | null
  attachments?: IAttachment[]
  createdAt: string | null
  html: string | null
  text: string | null
  type: string | null
  updatedAt: string | null
}

/**
 * The enum to indicate where the chat message event is sourced from
 */
export enum ChatMessageEventSourceEnum {
  AIGenerated = 'AI Generated',
  ChaineWebApp = 'Chaine Web App',
  Copilot = 'Copilot',
  DriverApp = 'Driver App',
  Email = 'Email',
  SMS = 'SMS',
  TMS = 'TMS',
  Unknown = 'Unknown'
}

/**
 * The enum to indicate who generated the chat message
 */
export enum ChatMessageGeneratedByEnum {
  Copilot = 'Copilot',
  GenAI = 'GenAI',
  Human = 'Human'
}

/**
 * The metadata for a Copilot rule and related information when the message is generated by Copilot
 */
export interface IChatMessageCopilotMetadata {
  ruleDescription: string
  ruleName: string
}

/**
 * The sender's role in the channel (e.g. 'Driver')
 */
export enum ShipmentChannelRoleEnum {
  Chain = 'Chain',
  Dispatcher = 'Dispatcher',
  Driver = 'Driver',
  TeamMember = 'Team Member',
  Unknown = 'Unknown'
}

export interface ISender {
  /**
   * The sender's role in the channel (e.g. 'Driver')
   */
  channelRole: ShipmentChannelRoleEnum

  /**
   * The display name of the sender. The logic for this is as follows:
   * 1. If the name is provided, use it
   * 2. If the name is not provided, use the email
   * 3. If the email is not provided, use the phone
   */
  displayName: string | null

  /**
   * The email of the sender.
   */
  email: string | null

  /**
   * This indicates if the user added is in the workspace that owns the shipment
   */
  isUserInChannelOwnerWorkspace: boolean

  /**
   * The sender's message role for Open AI (e.g. 'user')
   */
  messageRole: MessageRoleEnum

  /**
   * The user provided name of the sender.
   */
  name: string | null

  /**
   * The phone number of the sender.
   */
  phone: string | null

  /**
   * The UUID of the sender
   */
  userID: UUID

  /**
   * The UUID of the workspace that the sender belongs to.
   */
  workspaceID?: UUID
}

/**
 * The message role as it pertains to Open AI
 */
export enum MessageRoleEnum {
  Assistant = 'assistant',

  System = 'system',

  User = 'user'
}

export interface IMessage {
  /**
   * The attachments added to the message
   */
  attachments?: IAttachment[]

  /**
   * HTML content associated with the message
   */
  html: string | null

  /**
   * The UUID of the message
   */
  id: UUID

  /**
   * The message content
   */
  message: string | null

  /**
   * The source from which the message was generated (Human/Copilot/GenAI) {@link ChatMessageGeneratedByEnum}
   */
  messageGeneratedBy: ChatMessageGeneratedByEnum

  /**
   * The source from which the message was generated (Human/Copilot/GenAI) {@link ChatMessageGeneratedByEnum}
   */
  messageSource: ChatMessageEventSourceEnum

  /**
   * The information about the sender
   */
  senderDetails: ISender

  /**
   * The time the message was sent
   */
  sentAt: string | null
}
